/**
 * Stytch SDK errors put operator-facing copy inside `Error.message`: the status code, the
 * internal `error_type`, a dashboard URL, and the live request id. Rendering that verbatim on
 * the callback screen showed an invited user "[401] endpoint_not_authorized_for_sdk / The project
 * owner has not authorized the SDK... https://stytch.com/dashboard/sdk-configuration" and asked
 * them to fix something only we can fix (NIM-2464). Every message the callback screen shows is
 * produced here; an unrecognized failure falls back to generic copy rather than leaking its text.
 */

/** An error whose message was written for the end user and may be shown as-is. */
export class UserFacingAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingAuthError';
  }
}

const EXPIRED_LINK = 'This link is no longer valid. It may have already been used or expired.';
/**
 * Separate from `EXPIRED_LINK` on purpose. These failures are about the browser
 * session, and they can happen *after* an invitation has already been accepted,
 * where "this link is no longer valid" describes the wrong thing entirely and
 * tells someone who is now a member that they are not (NIM-5382).
 */
const SESSION_ENDED = 'Your sign-in session has ended. Please sign in again.';
const SERVICE_CONFIG = 'Sign-in is temporarily unavailable. Please try again later — if it keeps happening, let your organization admin know.';
const RATE_LIMITED = 'Too many attempts. Please wait a moment and try again.';
const NO_ACCESS = 'This account does not have access to that organization.';
const UNREACHABLE = 'We could not reach the sign-in service. Check your connection and try again.';

const COPY_BY_ERROR_TYPE: Record<string, string> = {
  // Project/SDK configuration problems — nothing the end user can act on.
  endpoint_not_authorized_for_sdk: SERVICE_CONFIG,
  bad_app_bundle_for_stytch_sdk: SERVICE_CONFIG,
  unauthorized_credentials: SERVICE_CONFIG,
  invalid_secret_authentication: SERVICE_CONFIG,
  invalid_project_id: SERVICE_CONFIG,

  // Spent, malformed, or expired links and sessions.
  magic_link_not_found: EXPIRED_LINK,
  unable_to_auth_magic_link: EXPIRED_LINK,
  magic_link_previously_used: EXPIRED_LINK,
  duplicate_magic_link: EXPIRED_LINK,
  invalid_token: EXPIRED_LINK,
  invalid_code: EXPIRED_LINK,

  // Session failures, which are not link failures.
  intermediate_session_not_found: SESSION_ENDED,
  session_not_found: SESSION_ENDED,
  invalid_session_token: SESSION_ENDED,
  user_unauthenticated: SESSION_ENDED,

  too_many_requests: RATE_LIMITED,

  member_not_found: NO_ACCESS,
  organization_not_found: NO_ACCESS,
  member_inactive: NO_ACCESS,
};

interface StytchApiErrorShape {
  error_type: string;
}

function stytchErrorType(cause: unknown): string | null {
  if (!cause || typeof cause !== 'object') return null;
  const errorType = (cause as Partial<StytchApiErrorShape>).error_type;
  if (typeof errorType === 'string' && errorType.length > 0) return errorType;
  // StytchAPIError instances always carry `error_type`; other Stytch errors only encode the
  // type in the `[status] error_type` first line of the message.
  const message = cause instanceof Error ? cause.message : '';
  const match = /^\[\d{3}\]\s+([a-z0-9_]+)/.exec(message);
  return match ? match[1]! : null;
}

function isNetworkFailure(cause: unknown): boolean {
  if (!(cause instanceof Error)) return false;
  return cause.name === 'StytchAPIUnreachableError' || cause.name === 'TypeError';
}

/**
 * Map any authentication failure to copy that is safe to render to an end user.
 *
 * `fallback` is the flow-specific generic message (invitation vs. sign-in) used when the failure
 * is not one we recognize.
 */
export function authErrorMessage(cause: unknown, fallback: string): string {
  if (cause instanceof UserFacingAuthError) return cause.message;
  if (isNetworkFailure(cause)) return UNREACHABLE;
  const errorType = stytchErrorType(cause);
  if (errorType && COPY_BY_ERROR_TYPE[errorType]) return COPY_BY_ERROR_TYPE[errorType]!;
  return fallback;
}

/**
 * The vocabulary an auth failure can be reported under.
 *
 * Derived from the copy table rather than restated, so a reason can only ever be
 * an error type this app already recognizes; anything else collapses to
 * `unrecognized`. That bound is what makes the value safe to send from the
 * callback route, whose URL carries a magic-link token — see
 * `invitationAnalyticsContract.ts`.
 */
export const AUTH_ERROR_REASONS = [
  ...Object.keys(COPY_BY_ERROR_TYPE),
  'network',
  'unrecognized',
] as const;

export type AuthErrorReason = (typeof AUTH_ERROR_REASONS)[number];

export function authErrorReason(cause: unknown): AuthErrorReason {
  if (isNetworkFailure(cause)) return 'network';
  const errorType = stytchErrorType(cause);
  return errorType && COPY_BY_ERROR_TYPE[errorType] ? errorType : 'unrecognized';
}
